// Phase SA-5 — Shared leg-builder types + pure helpers.
//
// Imported by StructuredLegBuilder, AutopsyForm, the Edge-fn paste
// fallback serializer, and the SA-5 tests.

import type { DeclaredOutcome } from "../../slips/useSlipAutopsy.ts";

/** A single leg as captured by the structured builder. Mirrors
 * brief §4.2 exactly. */
export interface StructuredLeg {
  /** UUID-ish local id — never sent to the Edge fn; the leg's
   * position in the array is what we persist. */
  local_id: string;
  fixture: string;
  market: string;
  selection: string;
  odds: number | null;
  outcome: LegOutcome;
}

/** Outcomes the builder supports per leg. NOT the same as the
 * overall slip outcome (the slip carries an aggregated
 * DeclaredOutcome). */
export type LegOutcome = "won" | "lost" | "void" | "unknown";

/** Master market list for the builder dropdown (brief §4.2). 16
 * entries in fixed order so the dropdown is scan-able. */
export const BUILDER_MARKETS: ReadonlyArray<string> = [
  "Match Winner",
  "Asian Handicap",
  "Draw No Bet",
  "Both Teams to Score",
  "Over/Under Goals",
  "Correct Score",
  "Half-Time Result",
  "HT/FT",
  "Double Chance",
  "Total Cards",
  "Player to be Carded",
  "Total Corners",
  "Anytime Goalscorer",
  "First Goalscorer",
  "Player Goals",
  "Other",
];

/** Bookmaker chips per brief §4.1. Slugs match the
 * slip_autopsy_queue.bookmaker column convention. */
export const BOOKMAKER_OPTIONS: ReadonlyArray<{
  slug: string;
  label: string;
}> = [
  { slug: "stake", label: "Stake" },
  { slug: "betway", label: "Betway" },
  { slug: "sportybet", label: "Sportybet" },
  { slug: "1xbet", label: "1xBet" },
  { slug: "other", label: "Other" },
];

/** Overall slip outcomes (matches DeclaredOutcome from
 * useSlipAutopsy). Exported for the OutcomeButtons component. */
export const SLIP_OUTCOMES: ReadonlyArray<{
  value: DeclaredOutcome;
  label: string;
}> = [
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "partial", label: "Partial" },
  { value: "void", label: "Void" },
  { value: "unknown", label: "Unknown" },
];

/** Pure: combined odds across all legs (excluding voids). Returns
 * null when no priced leg exists yet. */
export function combinedOdds(legs: ReadonlyArray<StructuredLeg>): number | null {
  const priced = legs.filter(
    (l) => l.outcome !== "void" && l.odds != null && Number.isFinite(l.odds) && l.odds > 0,
  );
  if (priced.length === 0) return null;
  return priced.reduce((acc, l) => acc * (l.odds as number), 1);
}

/** Pure: human-readable combined odds display. */
export function formatCombined(legs: ReadonlyArray<StructuredLeg>): string {
  const odds = combinedOdds(legs);
  if (odds == null) return "—";
  return `${odds.toFixed(2)}×`;
}

/** Pure: minimum-input validation for builder mode. A "valid" leg
 * has a fixture AND a market AND a non-null odds value. Selection
 * and outcome are required but the form defaults them. */
export function isLegComplete(l: StructuredLeg): boolean {
  return (
    l.fixture.trim().length > 0 &&
    l.market.trim().length > 0 &&
    l.selection.trim().length > 0 &&
    l.odds != null &&
    Number.isFinite(l.odds) &&
    l.odds > 0
  );
}

/** Pure: are ANY legs ready to submit? Used to gate the submit
 * button per brief AC-1.4. */
export function hasSubmittableLegs(legs: ReadonlyArray<StructuredLeg>): boolean {
  return legs.some((l) => isLegComplete(l));
}

/** Pure: serialize structured legs into a paste-mode text
 * representation. The Edge fn can parse this in SA-6 + earlier as
 * a fallback. Format: one leg per line, `Fixture · Market ·
 * Selection @ odds`. */
export function serializeLegs(legs: ReadonlyArray<StructuredLeg>): string {
  return legs
    .filter((l) => isLegComplete(l))
    .map(
      (l) =>
        `${l.fixture.trim()} · ${l.market} · ${l.selection.trim()} @ ${l.odds!.toFixed(2)}`,
    )
    .join("\n");
}

/** Pure: a fresh empty leg with a stable local_id. */
export function emptyLeg(): StructuredLeg {
  return {
    local_id: `leg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    fixture: "",
    market: BUILDER_MARKETS[0]!,
    selection: "",
    odds: null,
    outcome: "unknown",
  };
}
